import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, catchError, tap, switchMap, map } from 'rxjs';
import { Router } from '@angular/router';
import { UserModel } from '../models/app.models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserModel | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  private isInitializedSubject = new BehaviorSubject<boolean>(false);
  public isInitialized$ = this.isInitializedSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
  }

  public get currentUser(): UserModel | null {
    return this.currentUserSubject.value;
  }

  public get isInitialized(): boolean {
    return this.isInitializedSubject.value;
  }

  public initAuth(): Observable<boolean> {
    const token = localStorage.getItem('token');
    if (!token) {
      this.isInitializedSubject.next(true);
      return of(false);
    }

    return this.fetchUserInfo().pipe(
      map(user => !!user),
      tap(() => this.isInitializedSubject.next(true)),
      catchError(() => {
        this.isInitializedSubject.next(true);
        return of(false);
      })
    );
  }

  login(email: string, password: string): Observable<UserModel | null> {
    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('username', email);
    body.set('password', password);
    body.set('scope', 'offline_access');

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post<any>('/connect/token', body.toString(), { headers }).pipe(
      tap(response => {
          if (response && response.access_token) {
              localStorage.setItem('token', response.access_token);
          }
      }),
      switchMap(() => this.fetchUserInfo())
    );
  }

  logout() {
    localStorage.removeItem('token');
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  isLoggedIn(): boolean {
    return !!this.currentUserSubject.value;
  }

  fetchUserInfo(): Observable<UserModel | null> {
    // Endpoint na backendu, kde se token dešifruje
    return this.http.get<UserModel>('/api/account/userinfo').pipe(
      tap(user => {
        this.currentUserSubject.next(user);
      }),
      catchError(err => {
        console.error('Failed to fetch user info', err);
        return of(null);
      })
    );
  }
}
